import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, Clock, PhoneCall, ArrowRight, MousePointer2, ChevronDown } from "lucide-react";
import { scrollToId } from "@/pages/LandingPage";

const DEFAULT_LINES = ["Real lawyers.", "Real answers.", "One flat monthly plan."];
const DEFAULT_SUB =
  "Most sites hide their pricing and plans behind a lead form. We prefer to do things differently. Here is everything you need to know about getting a law firm on call, upfront and in plain English.";

const TOPICS = {
  lease: {
    lines: ["That lease clause is", "counting on you", "not reading it."],
    sub: "Security deposits, \u201cwear and tear,\u201d buried notice periods — leases are written by landlords' lawyers. For one flat monthly plan, a law firm reads yours before you sign. Real prices and full details, upfront, in plain English.",
  },
  terms: {
    lines: ["The \u201cI Agree\u201d button is", "the most expensive click", "you make."],
    sub: "Loans, warranties, contractor quotes, apps — the fine print is where the money leaks. A membership puts a law firm on call to read it before you agree, from $39.95 a month. Everything explained upfront.",
  },
  will: {
    lines: ["Most parents are", "one signature short of", "protecting their kids."],
    sub: "Without a will, a court decides guardianship — not you. A professionally prepared will is included in a membership from $39.95 a month, with a law firm on call for everything else. Real prices, upfront.",
  },
};

const topic = TOPICS[new URLSearchParams(window.location.search).get("t")];
const LINES = topic ? topic.lines : DEFAULT_LINES;
const SUB = topic ? topic.sub : DEFAULT_SUB;

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span
        initial={{ y: "110%" }}
        animate={{ y: 0 }}
        transition={{ delay, duration: 0.9, ease: EASE }}
        className="block"
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const cardY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative bg-[#0B192C] grain-overlay overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="absolute inset-0 hero-spotlight" />
      <div className="absolute -right-40 top-1/4 w-[560px] h-[560px] rounded-full border border-blue-500/10 pointer-events-none" />
      <div className="absolute -right-20 top-1/3 w-[360px] h-[360px] rounded-full border border-blue-400/10 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 pt-16 md:pt-24 pb-20 md:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="font-mono-accent text-xs uppercase tracking-[0.28em] text-sky-400 mb-8"
            data-testid="hero-eyebrow"
          >
            Membership-based legal access — explained first
          </motion.p>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-white" data-testid="hero-headline">
            <MaskedLine delay={0.35}>{LINES[0]}</MaskedLine>
            <MaskedLine delay={0.5}>{LINES[1]}</MaskedLine>
            <MaskedLine delay={0.65}>
              <span className="text-sky-400">{LINES[2]}</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8, ease: EASE }}
            className="mt-8 text-lg text-slate-300 leading-relaxed max-w-xl"
            data-testid="hero-subheadline"
          >
            {SUB}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8, ease: EASE }}
            className="mt-24 flex flex-wrap items-center gap-5"
          >
            <div className="relative">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2, duration: 0.6 }}
                className="absolute -top-[86px] left-4 pointer-events-none select-none"
                data-testid="start-here-cue"
              >
                <motion.div
                  animate={{ y: [0, 5, 0] }}
                  transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
                  className="flex flex-col items-center"
                >
                  <span
                    className="font-mono-accent text-emerald-400 text-sm font-semibold uppercase tracking-[0.3em] mb-1"
                    style={{ textShadow: "0 0 18px rgba(52, 211, 153, 0.5)" }}
                  >
                    Start here
                  </span>
                  <div className="flex flex-col items-center -space-y-3">
                    {[0, 1, 2].map((i) => (
                      <motion.span
                        key={i}
                        animate={{ opacity: [0.2, 1, 0.2] }}
                        transition={{ repeat: Infinity, duration: 1.5, delay: i * 0.25, ease: "easeInOut" }}
                      >
                        <ChevronDown size={26} color="#34D399" strokeWidth={3} />
                      </motion.span>
                    ))}
                  </div>
                </motion.div>
              </motion.div>
              <button
                data-testid="hero-cta-path"
                onClick={() => scrollToId("#your-path")}
                className="group flex items-center gap-3 bg-sky-400 hover:bg-sky-300 text-[#0B192C] font-semibold px-8 py-4 rounded-full transition-colors duration-300"
              >
                Find my path
                <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
            <p className="text-sm text-slate-400">30 seconds · no email needed to see your recommendation</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 0.8 }}
            className="mt-14 flex flex-wrap gap-x-10 gap-y-4"
            data-testid="hero-trust-row"
          >
            {[
              { icon: Clock, label: "24/7 emergency access" },
              { icon: ShieldCheck, label: "Nationwide provider network" },
              { icon: PhoneCall, label: "No hourly meter running" },
            ].map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5 text-slate-400 text-sm">
                <Icon size={16} className="text-sky-400" />
                {label}
              </div>
            ))}
          </motion.div>
        </motion.div>

        <div className="lg:col-span-5 relative hidden lg:block" data-testid="hero-visual">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 1, ease: EASE }}
            style={{ y: imgY }}
            className="relative"
          >
            <div className="absolute -inset-6 bg-blue-600/20 blur-3xl rounded-full" />
            <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl shadow-blue-950/60">
              <img
                src="/john-jones.png"
                alt="John Jones, your independent associate"
                className="w-full h-[480px] object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/70 via-transparent to-transparent" />
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.25, duration: 0.9, ease: EASE }}
            style={{ y: cardY }}
            className="absolute -bottom-8 -left-10 bg-white/95 backdrop-blur-md rounded-2xl p-5 shadow-xl shadow-blue-950/40 border border-white/60 max-w-[240px]"
            data-testid="hero-floating-card"
          >
            <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-blue-600 mb-2">The whole idea</p>
            <p className="font-display font-bold text-[#0B192C] leading-snug">Unlimited advice. No meter running while you talk.</p>
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="relative pb-8 flex justify-center"
      >
        <div className="flex items-center gap-3 rounded-full border border-white/10 bg-white/[0.05] backdrop-blur-sm pl-2 pr-6 py-2" data-testid="hero-associate-id">
          <img src="/john-jones.png" alt="John Jones, independent associate" className="w-10 h-10 rounded-full object-cover border-2 border-sky-400/60" />
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.2em] text-slate-400">
            Presented by <span className="text-slate-200">John Jones</span> — Independent Associate of LegalShield, not an employee
          </p>
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="relative pb-8 flex justify-center text-slate-500"
      >
        <MousePointer2 size={16} className="animate-bounce" />
      </motion.div>
    </section>
  );
}
